import { useCallback, useMemo, useContext } from 'react';
import Select, { type ActionMeta } from 'react-select';
import type { ContextFilter } from '@trinity/chat';
import {
  ActiveChatSessionContext,
  ChatContextItemsContext,
} from '@trinity/chat-components/components/Chat/context';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { resolveContextFilterValue } from './common';
import * as Presentation from './Presentation';

type Props = {
  value: ContextFilter.Value;
  onChange: (value: ContextFilter.Value, meta: ActionMeta<ContextFilter.Item>) => unknown;
};

export function Context({ value, onChange }: Props) {
  const theme = useTheme();
  const contextItems = useContext(ChatContextItemsContext);
  const { context: activeContext } = useContext(ActiveChatSessionContext);

  const selectStyles = useMemo(() => Presentation.buildSelectStyles(theme), [theme]);
  const selectTheme = useMemo(() => Presentation.buildSelectTheme(theme), [theme]);

  const options: ContextFilter.Item[] = useMemo(() => {
    if (!contextItems) return [];
    return contextItems
      .filter((ctx) => ctx.visibleInSelector)
      .map((ctx) => ({
        identifier: ctx.identifier,
        name: ctx.name,
      }));
  }, [contextItems]);

  const selected = useMemo(() => {
    // Fall back to the active session context when no value is selected
    const displayValue = value ?? (activeContext ? resolveContextFilterValue(activeContext, contextItems) : null);

    if (!displayValue) return null;
    // A context item hidden from the selector (visibleInSelector=false) is still the active one.
    return options.find((opt) => opt.identifier === displayValue.identifier) ?? displayValue;
  }, [value, options, activeContext, contextItems]);

  const handleChange = useCallback(
    (option: ContextFilter.Item | null, meta: ActionMeta<ContextFilter.Item>) => {
      onChange(option, meta);
    },
    [onChange]
  );

  return (
    <Presentation.Row>
      <Presentation.Label>Context</Presentation.Label>
      <Presentation.Value>
        <Select<ContextFilter.Item>
          styles={{
            ...selectStyles,
            control: (base) => ({
              ...base,
              minHeight: '38px',
              height: '38px',
            }),
            valueContainer: (base) => ({
              ...base,
              padding: '4px 8px',
            }),
            clearIndicator: (base) => ({
              ...base,
              padding: '4px',
            }),
            dropdownIndicator: (base) => ({
              ...base,
              padding: '4px',
            }),
          }}
          theme={selectTheme}
          options={options}
          value={selected}
          getOptionLabel={(o) => o.name}
          getOptionValue={(o) => o.identifier}
          // A chat always has exactly one context; clearing only reverted a pending pick, so no "x".
          isClearable={false}
          isSearchable={false}
          onChange={handleChange}
          menuPlacement="auto"
          maxMenuHeight={300}
          placeholder="Select a context..."
          isDisabled={!options || options.length === 0}
        />
      </Presentation.Value>
    </Presentation.Row>
  );
}
